import { APIService, type RequestBody } from '../api/apiService';
import type AuthenticatedUser from '../interfaces/AuthenticatedUser';
import type User from '../interfaces/User';
import type {
  CreateUserPayload,
  GetUsersPayload,
  GetUsersResponse,
} from '../interfaces/UserService';

export class UserService extends APIService {
  /**
   * @desc Get user info
   * @return {Promise<*>
   *     }
   */
  getUserInfo(): Promise<AuthenticatedUser> {
    return this.request('/user/auth/data', 'GET');
  }

  /**
   * Sends a request to generate an OTP for the user.
   *
   * @param {RequestBody} payload - The payload containing user information required to generate the OTP.
   * @return {Promise<any>} - A promise that resolves to the server's response.
   */
  requestUserOtp(payload: RequestBody) {
    return this.request('/user/otp/v2', 'POST', payload);
  }

  /**
   * Authenticates a user using provided credentials.
   *
   * @param {RequestBody} payload - Object containing user login credentials.
   * @return {Promise<Response>} A promise that resolves with the server response.
   */
  login(payload: RequestBody): Promise<{
    token: string;
  }> {
    return this.request('/user/auth/v2', 'POST', payload);
  }

  /**
   * @desc Get the list of users
   * @param payload
   * @return {Promise<* | undefined>}
   */
  getUsers(payload: GetUsersPayload): Promise<GetUsersResponse> {
    return this.request('/listusers', 'POST', { ...payload });
  }

  /**
   * @desc Get a specific user
   * @param id
   * @return {Promise<* | undefined>}
   */
  getUser(id: number): Promise<User> {
    return this.request(`/user/${id}`, 'GET');
  }

  /**
   * @desc Update a user
   * @param payload
   * @return {Promise<* | undefined>}
   */
  createUser(payload: CreateUserPayload): Promise<User> {
    return this.request('/user', 'POST', { ...payload });
  }

  /**
   * @desc Update a user
   * @param id
   * @param payload
   * @return {Promise<* | undefined>}
   */
  updateUser(payload: User) {
    return this.request(`/user/${payload.id}`, 'PUT', { ...payload });
  }

  /**
   * @desc Change the status of users
   * @param ids
   * @param status
   * @return {Promise<* | undefined>}
   */
  changeUsersStatus(ids: number[], status: 0 | 1) {
    return this.request(`/user/status?value=${status}`, 'PUT', ids);
  }

  /**
   * @desc Delete users
   * @param ids
   * @param query
   * @return {Promise<* | undefined>}
   */
  deleteUsers(ids: number[]) {
    return this.request(`/user`, 'DELETE', ids);
  }

  /**
   * @desc Delete user groups
   * @return {Promise<void}
   * @param id
   * @param groupIds
   */
  deleteUserGroups(id: number, groupIds: number[]): Promise<void> {
    return this.request(`/user/${id}/groups`, 'DELETE', groupIds);
  }
}
